import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Link,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { sortBy } from "lodash";
import { useEffect, useRef } from "react";
import { useScroll } from "../../../../../../contexts/ScrollContext";
import { useCopilot } from "../../../../../../hooks/useCopilot";
import { formatDateTimeWithSeconds } from "../../../../../../utils/DateUtils";

type Props = {
  isRecording: boolean;
  onNavigateToNote?: () => void;
};

const Transcript = ({ isRecording, onNavigateToNote }: Props) => {
  const { copilotData, isProcessingTranscript, liveTranscript, audioLevel } =
    useCopilot();
  const { setBottomContainerRef, isAutoScroll, setScrollFreezeUntil } =
    useScroll();
  const transcripts = copilotData?.transcripts;
  const textContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const groupSize = 10;
  const groupedTranscripts = transcripts
    ? Array.from(
        { length: Math.ceil(transcripts.length / groupSize) },
        (_, i) =>
          sortBy(transcripts, ["period.start"]).slice(
            i * groupSize,
            (i + 1) * groupSize,
          ),
      )
    : [];

  useEffect(() => {
    setBottomContainerRef(bottomRef);
  }, [setBottomContainerRef]);

  useEffect(() => {
    if (isAutoScroll && isRecording) {
      const container = document.getElementById("copilot-scroll-container");
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
        setScrollFreezeUntil(1000);
      }
    }
  }, [transcripts, liveTranscript, isAutoScroll, isRecording]);

  return (
    <Box minHeight={"200px"}>
      <Grid container spacing={2} ref={textContainerRef}>
        {!isRecording && !isProcessingTranscript && !copilotData?.note && (
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <WarningAmberIcon color="warning" />
              <i>
                No notes generated yet. Proceed to the{" "}
                <Link
                  component="button"
                  variant="subtitle1"
                  onClick={onNavigateToNote}
                  sx={{
                    fontWeight: "bold",
                    fontStyle: "italic",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  AI Note
                </Link>{" "}
                tab and click '<b>Generate Note</b>' to create a note from the
                transcript.
              </i>
            </Typography>
          </Grid>
        )}
        {groupedTranscripts?.map((group, groupIndex) => {
          const isLastGroup = groupIndex === groupedTranscripts.length - 1;
          const transcriptText = group
            ?.map(
              (t, index) =>
                t?.text +
                (isLastGroup && index === group.length - 1
                  ? liveTranscript
                  : ""),
            )
            .join(" ");
          if (transcriptText?.trim()) {
            return (
              <Grid item xs={12} key={`transcript-group-${groupIndex}`}>
                <Typography variant="subtitle2">
                  {formatDateTimeWithSeconds(new Date(group[0]?.start))} to{" "}
                  {formatDateTimeWithSeconds(
                    new Date(group[group.length - 1]?.end),
                  )}
                </Typography>
                <Typography variant="body2">{transcriptText}</Typography>
              </Grid>
            );
          }
          return null;
        })}

        {/* Processing indicator */}
        {isProcessingTranscript && (
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                backgroundColor: "rgba(33, 150, 243, 0.08)",
                border: "2px solid rgba(33, 150, 243, 0.3)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={48} thickness={4} />
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  Processing Transcript
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we finalise your transcription...
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}

        {isRecording && (
          <Grid item xs={12}>
            <Typography
              variant="caption"
              color="error.main"
              sx={{ fontWeight: 500, fontSize: 16 }}
              aria-live="polite"
            >
              Listening…
            </Typography>
          </Grid>
        )}

        <Box ref={bottomRef} />
      </Grid>
    </Box>
  );
};

export default Transcript;
